'use client'
import { useEffect, useLayoutEffect, useState } from 'react'
import { useInit } from 'Hooks/useInit'
import { useInitLanguage } from 'Hooks/useInitLanguage'
import ExternalBrowserPrompt from 'Comps/compatibility/ExternalBrowserPrompt'
import { storageKey } from '@/constant/storage'
import { readUserId } from '@/constant/prefetch-user'
import { uuid } from 'Utils/func'
import { useQuickTracking } from '@/provider/QuickTrackingProvider'
import { useRedirectSignInFallback } from '@/hooks/useRedirectSignInFallback'
import {
  APP_PERFORMANCE_MARKS,
  markPerformanceStage,
} from '@/utils/firstScreenPerformance'

// 客户端模块开始执行；相比 effect 更接近 Next 主脚本完成下载后的执行起点。
markPerformanceStage(APP_PERFORMANCE_MARKS.clientEntryStarted)

export default function ClientInit() {
  const [externalBrowserPromptVisible, setExternalBrowserPromptVisible] =
    useState(false)
  const [externalBrowserTitle, setExternalBrowserPromptTitle] = useState(
    'Open in external browser'
  )
  const { setUserId, trackEvent } = useQuickTracking()

  useInit()
  useInitLanguage()
  // popup 被拦时 redirect 兜底登录的回跳入口（无回跳结果时零副作用）。
  useRedirectSignInFallback()

  useLayoutEffect(() => {
    // layout effect 表示 React 已完成首轮 hydration commit，可开始读取真实布局。
    markPerformanceStage(APP_PERFORMANCE_MARKS.reactHydrationCommitted)
  }, [])

  // 测试环境不用开启 google analytics
  // useEffect(() => {
  //   analytics
  // }, [])
  useEffect(() => {
    if (typeof window !== 'undefined') {
      let sessionId = sessionStorage?.getItem?.(storageKey.SESSION_ID)
      if (!sessionId) {
        sessionId = uuid()
        sessionStorage?.setItem?.(storageKey.SESSION_ID, sessionId)
      }
    }
  }, [])
  useEffect(() => {
    // 检查sessionStorage是否已经记录了着陆页面
    if (!sessionStorage.getItem('landingPathname')) {
      // 将当前路由路径存储为着陆路由
      sessionStorage.setItem('landingPathname', location.pathname)
    }
  }, [])
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // traceId 用于风控追踪用户
      let traceId = localStorage?.getItem?.(storageKey.TRACE_ID)
      if (!traceId) {
        traceId = uuid()
        localStorage?.setItem?.(storageKey.TRACE_ID, traceId)
      }
    }
  }, [])
  useLayoutEffect(() => {
    // TODO: 增加所有需要跳转到外部浏览器的 UserAgent
    const userAgent = window.navigator.userAgent
    const externalBrowserUserAgents = [
      { regex: /Instagram/i, title: 'Open in external browser' },
      { regex: /TikTok/i, title: 'Open in browser' },
      { regex: /musically/i, title: 'Open in browser' },
      { regex: /musical_ly/i, title: 'Open in browser' },
    ]

    // 检查当前User-Agent是否匹配任意一个需要跳转的条件
    const matchedAgent = externalBrowserUserAgents.find((agent) =>
      agent.regex.test(userAgent)
    )

    if (matchedAgent) {
      setExternalBrowserPromptTitle(matchedAgent.title)
      setExternalBrowserPromptVisible(true)
      document.title = 'Tipsy Chat - Your AI Tavern with Temperature'
      document
        ?.querySelector('meta[name="description"]')
        ?.setAttribute(
          'content',
          'Join us at Tipsy Tavern, where imagination knows no bounds, and every sip brings you closer to the realization of your deepest desires.'
        )
    }
  }, [])

  useEffect(() => {
    // localStorage 状态清理
    localStorage?.removeItem('hasShowLaunchCelebrationModal')
    localStorage?.removeItem('hasCloseAnnualPlanModal')
  }, [])

  useEffect(() => {
    // 检查并设置已登录用户的 user_id（复用 readUserId 单一解析逻辑，与预取 dedup 口径一致）。
    const userId = readUserId()
    if (userId) {
      setUserId(userId)
    }
  }, [setUserId])

  useEffect(() => {
    // 接收 SW API 预取度量并转发 QuickTracking。
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return
    }
    const onMessage = (event: MessageEvent) => {
      const message = event.data as
        | { type?: string; data?: Record<string, unknown> }
        | undefined
      if (message?.type !== 'PREFETCH_METRIC' || !message.data) return
      trackEvent('sw_prefetch', message.data, 'OTHER')
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage)
    }
  }, [trackEvent])

  return (
    externalBrowserPromptVisible && (
      <ExternalBrowserPrompt
        title={externalBrowserTitle}
        onClose={() => setExternalBrowserPromptVisible(false)}
      />
    )
  )
}
